import React, { useState, useEffect } from 'react';
import { X, Minimize2, Maximize2, Maximize, Minimize, MessageCircle } from 'lucide-react';
import { useChatSupport } from '../contexts/ChatSupportContext';
import { useQuickStartAssistant } from '../contexts/QuickStartAssistantContext';
import { useTheme } from '../contexts/ThemeContext';
import AssistantConversation from './assistant/AssistantConversation';
import ConversationHistoryMenu from './assistant/ConversationHistoryMenu';

/**
 * The floating chat bubble — now hosts the same tool-calling Arie thread as
 * the Quick Start modal (one assistant, two entry points; see
 * ASSISTANT_AGENT_PLAN.md Phase 1c). Window chrome (open/close, minimize,
 * fullscreen) still comes from ChatSupportContext — that's pure UI state
 * unrelated to messaging. The old conversational "Strategic Risk Consul"
 * messaging path (sendMessage/messages/attachedContext) in
 * ChatSupportContext is no longer called from here; it stays mounted but
 * unused until Phase 6 removes it. The General/Agency Knowledge toggle now
 * lives in AssistantConversation's composer (knowledgeMode on
 * QuickStartAssistantContext — see AGENCY_KNOWLEDGE_MODE_PLAN.md).
 *
 * Closing this window does NOT clear the conversation — closing is not
 * deleting (matches the modal). Starting over is an explicit choice via
 * ConversationHistoryMenu's "+ New chat".
 */
const SupportChatWindow: React.FC = () => {
  const { isOpen, closeChat } = useChatSupport();
  const { messages } = useQuickStartAssistant();

  const { isDarkMode } = useTheme();
  const [isMinimized, setIsMinimized] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Handle keyboard shortcuts for fullscreen
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (isOpen && !isMinimized && e.key === 'F11') {
        e.preventDefault();
        setIsFullscreen(!isFullscreen);
      }
      if (isOpen && !isMinimized && e.key === 'Escape' && isFullscreen) {
        e.preventDefault();
        setIsFullscreen(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, isMinimized, isFullscreen]);

  if (!isOpen) return null;

  return (
    <div className={`fixed z-40 transition-all duration-300 ${
      isFullscreen
        ? 'inset-0'
        : isMinimized
          ? 'bottom-24 right-6 w-64'
          : 'bottom-24 right-6 w-96'
    }`}>
      <div className={`${
        isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
      } ${isFullscreen ? '' : 'rounded-lg shadow-2xl border'} flex flex-col ${
        isFullscreen
          ? 'h-full w-full'
          : isMinimized
            ? 'h-14'
            : 'h-[600px]'
      } transition-all duration-300`}>

        {/* Header */}
        <div className={`flex items-center justify-between px-4 py-3 border-b ${
          isFullscreen ? '' : 'rounded-t-lg'
        } ${
          isDarkMode ? 'bg-gray-900 border-gray-700 text-white' : 'bg-blue-500 text-white border-blue-600'
        }`}>
          <div className="flex items-center gap-2">
            <MessageCircle className="w-5 h-5" />
            <span className="font-medium">Arie</span>
          </div>
          <div className="flex items-center gap-2">
            {!isMinimized && <ConversationHistoryMenu theme="onDark" />}
            {!isMinimized && (
              <button
                onClick={() => setIsFullscreen(!isFullscreen)}
                className="hover:opacity-75 transition-opacity"
                title={isFullscreen ? "Exit fullscreen (F11 or Esc)" : "Enter fullscreen (F11)"}
              >
                {isFullscreen ? <Minimize className="w-4 h-4" /> : <Maximize className="w-4 h-4" />}
              </button>
            )}
            <button
              onClick={() => {
                setIsMinimized(!isMinimized);
                if (isFullscreen) setIsFullscreen(false); // Exit fullscreen when minimizing
              }}
              className="hover:opacity-75 transition-opacity"
              title={isMinimized ? "Expand chat" : "Minimize chat"}
            >
              {isMinimized ? <Maximize2 className="w-4 h-4" /> : <Minimize2 className="w-4 h-4" />}
            </button>
            <button
              onClick={() => {
                closeChat();
                setIsFullscreen(false);
              }}
              className="hover:opacity-75 transition-opacity"
              title="Close (conversation is saved)"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {!isMinimized && (
          <div className="flex-1 min-h-0">
            <AssistantConversation variant="bubble" autoFocus={messages.length === 0} onNavigated={closeChat} />
          </div>
        )}
      </div>
    </div>
  );
};

export default SupportChatWindow;
